import { ChatCommissionPaymentType, ChatCommissionRate, ChatCompany } from './types'

export interface CommissionBank {
  bankName: string
  interestRate: number
}

export interface CommissionInstallmentGroup {
  installment: number
  banks: CommissionBank[]
}

/**
 * Seçili şirket + ödeme tipinin düz oran satırlarını taksit bloklarına çevirir.
 * Backend sırasına güvenilmez: taksitler artan, bankalar oranı artan sıralanır — her grubun
 * `banks[0]`'ı o taksitin en uygunudur ("En uygun" modu bunu, "Tümü" modu grubun tamamını gösterir.)
 * Oran eşitliğinde sıralama banka adına (tr locale) göre yapılır — aksi halde "en uygun" banka
 * backend'in gönderdiği satır sırasına göre payloaddan payloada değişebilir.
 * Aynı banka + taksit birden fazla satırda gelebilir (portal verisinde PaymentTermsId ek bir
 * boyut); bu durumda en düşük oran tutulur, kartta aynı banka iki kez görünmez.
 */
export function groupCommissionRates(
  rates: ChatCommissionRate[],
  companyCode: string,
  paymentTypeCode: string
): CommissionInstallmentGroup[] {
  const byInstallment = new Map<number, Map<string, number>>()

  for (const rate of rates) {
    if (rate.companyCode !== companyCode || rate.paymentTypeCode !== paymentTypeCode) continue

    let banks = byInstallment.get(rate.installment)
    if (!banks) {
      banks = new Map<string, number>()
      byInstallment.set(rate.installment, banks)
    }

    const current = banks.get(rate.bankName)
    if (current === undefined || rate.interestRate < current) banks.set(rate.bankName, rate.interestRate)
  }

  return Array.from(byInstallment.entries())
    .sort(([a], [b]) => a - b)
    .map(([installment, banks]) => ({
      installment,
      banks: Array.from(banks.entries())
        .map(([bankName, interestRate]) => ({ bankName, interestRate }))
        .sort((a, b) => a.interestRate - b.interestRate || a.bankName.localeCompare(b.bankName, 'tr')),
    }))
}

/**
 * Bir taksit grubunda EN UYGUN oranı paylaşan bankaların HEPSİ. Oran eşitliğinde bankalardan
 * birini seçip diğerlerini gizlemek bayiye eksik bilgi verir (aynı oranı veren alternatif POS /
 * banka da geçerli bir seçenektir), bu yüzden "En uygun" görünümü tek satır değil bu listedir.
 * `banks` oranı artan sıralı olduğu için ilk elemanın oranı grubun en uygunudur.
 */
export function bestBanks(group: CommissionInstallmentGroup): CommissionBank[] {
  const best = group.banks[0]?.interestRate
  return best === undefined ? [] : group.banks.filter(bank => bank.interestRate === best)
}

/**
 * En az bir sekmesi çizilebilir oranı olan şirketler (payload sırası korunur).
 * Yalnız companyCode eşleşmesi yetmez: şirketin tüm oranları paymentTypes sözlüğünde karşılığı
 * olmayan bir tip kullanıyorsa (ör. etiketsiz bir Type kodu) o şirket seçildiğinde chip listesi
 * boşalır ve kart tamamen kaybolur — bu yüzden şirketin en az bir *etiketli* oranı olmalı.
 */
export function getAvailableCompanies(
  rates: ChatCommissionRate[],
  companies: ChatCompany[],
  paymentTypes: ChatCommissionPaymentType[]
): ChatCompany[] {
  const knownTypes = new Set(paymentTypes.map(paymentType => paymentType.code))
  const withRates = new Set(rates.filter(rate => knownTypes.has(rate.paymentTypeCode)).map(rate => rate.companyCode))
  return companies.filter(company => withRates.has(company.code))
}

/** Seçili şirkette en az bir oranı olan ödeme tipleri — karşılıksız chip gösterilmez. */
export function getAvailablePaymentTypes(
  rates: ChatCommissionRate[],
  paymentTypes: ChatCommissionPaymentType[],
  companyCode: string
): ChatCommissionPaymentType[] {
  const withRates = new Set(rates.filter(rate => rate.companyCode === companyCode).map(rate => rate.paymentTypeCode))
  return paymentTypes.filter(paymentType => withRates.has(paymentType.code))
}

/** Varsayılan ödeme tipi: isDefault işaretli olan, yoksa ilki. Liste boşsa boş string. */
export function getDefaultPaymentTypeCode(paymentTypes: ChatCommissionPaymentType[]): string {
  return (paymentTypes.find(paymentType => paymentType.isDefault) ?? paymentTypes[0])?.code ?? ''
}

export function formatInstallmentLabel(installment: number): string {
  return installment <= 1 ? 'Tek çekim' : `${installment} taksit`
}
